import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import { Spinner, Text } from 'theme-ui';
import {
    useGetIncentiveActionsQuery,
    useGetIncentivesQuery,
    useGetLicenseTypesQuery,
    usePutIncentiveMutation,
} from '@/store/apiService';
import { useParams } from 'react-router-dom';
import Error from '@/components/Error';
import { useNavigate } from 'react-router-dom';
import { Incentive, IncentiveActionType } from '@/types/types';
import { toast } from 'react-toastify';
import IncentiveForm, { validateIncentiveDates } from './IncentiveForm';
import { omit } from 'lodash';

type Props = {};

const DEFAULT_ACTION_ID = IncentiveActionType.Information;

const EditIncentive: React.FC<Props> = (props: Props) => {
    const { incentiveId } = useParams<{ incentiveId: string }>();
    const [editIncentive, editIncentiveState] = usePutIncentiveMutation();
    const incentivesState = useGetIncentivesQuery({
        // @ts-ignore
        Id: incentiveId,
        IncludeAction: true,
        IncludeCriteria: true,
        IncludeCompanies: true,
    });
    const licenseTypes = useGetLicenseTypesQuery({});
    const [actionId, setActionId] = useState<IncentiveActionType>(DEFAULT_ACTION_ID);
    const actionsState = useGetIncentiveActionsQuery({});

    const licenseTypesOptions = licenseTypes.data
        ? licenseTypes.data.Results.map((licenceType) => ({
            // @ts-ignore
            value: licenceType.Id,
            label: licenceType.Name,
        }))
        : [];
    let navigate = useNavigate();

    // @ts-ignore
    const incentive: Incentive = incentivesState.data?.Results.find(
        // @ts-ignore
        (i) => i.Id === Number(incentiveId)
    );

    useEffect(() => {
        if (incentive) {
            setActionId(incentive.ActionId);
        }
    }, [incentive]);

    if (actionsState.isLoading || incentivesState.isLoading) {
        return <Spinner size={16} />;
    }

    if (!incentive) {
        return <Text>No incentive found</Text>;
    }

    // TODO: list all licenses and add tickboxes for inverted
    return (
        <>
            <Formik
                enableReinitialize
                validate={validateIncentiveDates}
                initialValues={{
                    Id: incentive.Id,
                    Active: incentive.Active,
                    Heading: incentive.Heading,
                    StorageUrl: incentive.StorageUrl,
                    Frequency: incentive.Frequency,
                    InitialDelayInSeconds: incentive.InitialDelayInSeconds,
                    // @ts-ignore add after types are genarated
                    InitialDeferredDays: incentive.InitialDeferredDays,
                    MaxDisplayCount: incentive.MaxDisplayCount,
                    SuccessButtonText: incentive.SuccessButtonText,
                    // Normalise empty dates to null in the form state
                    ValidFrom: incentive.ValidFrom || null,
                    ValidTo: incentive.ValidTo || null,
                    Body: '',
                    RecurrenceInterval: incentive.RecurrenceInterval,
                    CompanyIds: incentive.CompanyIds ? incentive.CompanyIds : [],
                    Criteria: incentive.Criteria,
                    ActionId: incentive.ActionId ? incentive.ActionId : DEFAULT_ACTION_ID,
                    _content: incentive.StorageUrl ? 'storage' : 'file',
                }}
                onSubmit={(values, { resetForm, setErrors, setSubmitting }) => {
                    const normalizeDateForApi = (value: string | null | undefined) => {
                        return value ? value : null;
                    };

                    const sanitizedFormValues: any = {
                        ...values,
                        // @ts-ignore
                        Criteria: values.Criteria.filter((c) => c.checked),
                    };

                    sanitizedFormValues.ValidFrom = normalizeDateForApi(values.ValidFrom as any);
                    sanitizedFormValues.ValidTo = normalizeDateForApi(values.ValidTo as any);

                    editIncentive(sanitizedFormValues)
                        .unwrap()
                        .then((res) => {
                            navigate(`/incentives`);
                        })
                        .catch((err) => {
                            setSubmitting(false);
                            toast.error(err.data, { autoClose: 5000 });
                        });
                }}
            >
                {(formProps) => {
                    return (
                        <IncentiveForm
                            actionId={actionId}
                            setActionId={setActionId}
                            licenseTypesOptions={licenseTypesOptions}
                            additionalErrors={editIncentiveState.error}
                            actions={actionsState.data.Results}
                            {...formProps}
                        />
                    );
                }}
            </Formik>
        </>
    );
};

export default EditIncentive;
